import React, { Fragment } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import compose from 'lodash.flowright';
import { withRouter } from 'react-router-dom';
import { graphql } from 'react-apollo';

import useFeaturePermission, {
  FEATURE_PERMISSION_TYPE,
} from '../../../../services/featurePermissions';
import WeddingEventFeatureUpsell from '../../../../views/AlbumFolder/WeddingEvents/WeddingEventFeatureUpsell';
import {
  PURCHASED_EVENT_ADDON_FEATURES,
  PURCHASED_EVENT_FEATURES,
  SEARCH_SUBSCRIPTION_FEATURES,
} from '../../../../services/featurePermissions/helpers';
import WaldoficationFeatureUpsellBanner from '../../../../../../components/global/WaldoficationFeatureUpsell/components/WaldoficationFeatureUpsellBanner';
import MarketingMaterials from '../MarketingMaterials';
import {
  AlbumFolderForContributorShare,
  AlbumForContributorShare,
} from '../../../../../../graphql/queries/contributors';
import { isAlbumIndividual } from '../../../../../../util/album';
import {
  ALBUM_SLIDESHOW_STATUS,
  ALBUM_SUBTYPE,
  ALBUM_TYPE,
} from '../../../../../../constants/albums';
import Button from '../../../../../../components/global/Button';
import AssetDownloadButton from '../../../../views/AlbumFolder/AlbumFolderMarketing/components/AssetDownloadButton';
import { MARKETING_MATERIAL_TYPE } from '../../../../views/AlbumFolder/AlbumFolderMarketing/constants';
import useAlbumFolderMarketingAssets from '../../../../views/AlbumFolder/AlbumFolderMarketing/components/AlbumFolderMarketingList/useAlbumFolderMarketingAssets';
import MarketingMaterialSampleModal from '../MarketingMaterialSample';
import Spinner from '../../../../../../components/global/Spinner';

const propTypes = {
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
  }),
  album: PropTypes.shape({
    id: PropTypes.string,
    album_folder: PropTypes.shape({
      id: PropTypes.string,
    }),
  }),
  headless: PropTypes.bool,
  footerless: PropTypes.bool,
  shareable: PropTypes.bool,
};

const MarketingMaterialsShare = ({
  albumFolder,
  album,
  headless = false,
  footerless = false,
  shareable = true,
}) => {
  const entity =
    isAlbumIndividual(album) ||
    album?.is_video_album ||
    album?.slideshow?.status === ALBUM_SLIDESHOW_STATUS.PUBLISHED ||
    (album?.album_type === ALBUM_TYPE.EVENT &&
      album?.album_subtype !== ALBUM_SUBTYPE.WEDDINGS)
      ? album
      : (albumFolder ?? album);

  const { marketingMaterials } = useAlbumFolderMarketingAssets({
    albumFolderId: albumFolder?.id,
  });

  const QRCodeMarketingMaterial = marketingMaterials?.find(
    marketingMaterial =>
      marketingMaterial.type === MARKETING_MATERIAL_TYPE.QR_CODE_SHARE,
  );

  const featurePermissionItem = useFeaturePermission({
    albumId: album?.id,
    albumFolderId: albumFolder?.id ?? album?.root_album_folder?.id,
    features: [
      SEARCH_SUBSCRIPTION_FEATURES.qr_code_enabled,
      SEARCH_SUBSCRIPTION_FEATURES.marketing_prints_enabled,
    ],
  });

  if (!entity) {
    return (
      <div className="d-flex p-5 align-items-center justify-content-center">
        <Spinner className="m-auto" />
      </div>
    );
  }

  return (
    <Fragment>
      {!headless && (
        <Fragment>
          <h3 className="text-center">QR code signage</h3>
          <hr className="mt-0 mb-1" />
        </Fragment>
      )}
      {!featurePermissionItem.qr_code_enabled.enabled &&
        featurePermissionItem.type ===
          FEATURE_PERMISSION_TYPE.PURCHASED_EVENT_FEATURES && (
          <WeddingEventFeatureUpsell
            albumFolderId={albumFolder?.id ?? album?.root_album_folder?.id}
            featureId={PURCHASED_EVENT_FEATURES.qr_code_enabled}
            banner
            className="mt-2"
          />
        )}
      {!featurePermissionItem.qr_code_enabled.enabled &&
        featurePermissionItem.type ===
          FEATURE_PERMISSION_TYPE.PURCHASED_EVENT_ADDON_FEATURES && (
          <WaldoficationFeatureUpsellBanner
            albumFolderId={albumFolder?.id ?? album?.root_album_folder?.id}
            albumSubType={
              (albumFolder ?? album?.root_album_folder)?.album_subtype
            }
            featureId={PURCHASED_EVENT_ADDON_FEATURES.qr_code_enabled}
            albumId={album?.id}
            className="mt-2"
            withModal={false}
          />
        )}
      <MarketingMaterials
        isOwner
        entity={entity}
        shareable={shareable}
        albumFolderId={
          entity === album ? entity?.root_album_folder?.id : entity?.id
        }
      />
      {featurePermissionItem.qr_code_enabled.enabled && !footerless && (
        <Fragment>
          <hr className="mt-0 mb-1" />
          <p className="text-center text-light text-sm">
            Looking for something more custom?
          </p>
          <div className="d-flex">
            {QRCodeMarketingMaterial && (
              <AssetDownloadButton
                btnBlock
                btnOutline
                btnSize="sm"
                accountMarketingMaterialId={QRCodeMarketingMaterial?.id}
                assetKey="image_download_url"
                label="Download QR"
                className="mr-1"
              />
            )}
            <Button
              btnBlock
              btnOutline
              btnSize="sm"
              target="_blank"
              rel="noopener noreferrer"
              href={
                entity?.album_subtype === ALBUM_SUBTYPE.WEDDINGS
                  ? 'https://waldophotos.com/weddings/canva-templates/'
                  : 'https://waldophotos.com/waldoevents-canva-templates/'
              }
              className={classNames({
                'mt-0': true,
                'ml-1': !!QRCodeMarketingMaterial,
              })}
            >
              View Canva templates
            </Button>
          </div>
        </Fragment>
      )}
      <MarketingMaterialSampleModal />
    </Fragment>
  );
};

MarketingMaterialsShare.propTypes = propTypes;

const withAlbumForContributorShare = graphql(AlbumForContributorShare, {
  options: ({ albumId, match }) => ({
    variables: {
      id: albumId ?? match.params.albumId,
    },
  }),
  props: ({ data: { album, loading } }) => ({
    album,
    albumFolder: album?.album_folder,
    loading,
  }),
  skip: ({ albumId, match }) => !albumId && !match.params.albumId,
});

const withAlbumFolderForContributorShare = graphql(
  AlbumFolderForContributorShare,
  {
    options: ({ albumFolderId, match }) => ({
      variables: {
        id: albumFolderId ?? match.params.folderId,
      },
    }),
    props: ({ data: { albumFolder, loading } }) => ({
      albumFolder,
      loading,
    }),
    skip: ({ albumFolderId, match }) =>
      !albumFolderId && !match.params.folderId,
  },
);

export default compose(
  withRouter,
  withAlbumForContributorShare,
  withAlbumFolderForContributorShare,
)(MarketingMaterialsShare);
